// InstructionFormatPanel.js
// ─────────────────────────────────────────────────────────────────────────────
// PER-PROJECT SITE INSTRUCTION FORMAT
//
// Every client formats site instructions differently. This module keeps the
// DATA MODEL canonical (siNumber, dates, subject, instructionText, financial /
// as-built implications, valuation basis — those never change, so registers,
// dashboards and meeting snapshots keep working across projects) while making
// the FIELDS and PRINT LAYOUT configurable per project.
//
// Config lives on the project doc:   projects/{projectId}.instructionConfig
//
//   {
//     documentTitle: "Site Instruction",
//     numberingFormat: "SI-{seq:3}",          // {seq} {seq:N} {yyyy} {contract}
//     customFields: [
//       { key, label, type, options?, showInPrint }   // type: text | textarea |
//     ],                                              // date | number | select |
//     print: {                                        // checkbox
//       layout: "tabular" | "letter",
//       headerLines: ["EMPLOYER / CLIENT NAME", "…"],
//       signatureLeftLabel: "EMPLOYER'S AGENT",
//       signatureRightLabel: "CONTRACTOR'S REPRESENTATIVE",
//       footerClause: "Issued in terms of clause 5.10 of the GCC…",
//     },
//   }
//
// Custom field VALUES are stored on each instruction doc under
// `customFields: { key: value }` — handleAddInstruction already spreads the
// whole form into the doc, so no App.js handler changes are required.
// Custom fields are record/display only — they NEVER feed financial maths.
//
// Exports:
//   default  InstructionFormatPanel   — config editor modal + AI format import
//   CustomFieldsInputs                — config-driven inputs for the SI form
//   DEFAULT_INSTRUCTION_CONFIG
//   getInstructionConfig(currentProject)
//   formatSiNumber(config, seq, currentProject)
//   suggestNextSiNumber(config, instructions, currentProject)
//   printInstruction(row, config, currentProject)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { appCheckHeader } from "../utils/appCheck";
import { openHtmlWindow } from "../utils/printDoc";
import { tokens } from "./theme";
import { Input, Select, Textarea, FormRow, Button } from "./ui";

const t = tokens;
const C = tokens.color;
const MONO = '"JetBrains Mono", monospace';

// ─── Canonical model — never configurable ────────────────────────────────────

const CANONICAL_FIELDS = [
  "siNumber", "projectNumber", "contractNumber", "contractDescription",
  "contractor", "issuedBy", "subject", "instructionText", "instructionDate",
  "instructionTime", "receivedBy", "receivedDate", "receivedTime",
  "financialImplications", "asBuiltImplications", "valuationBasis",
  "applicablePayItems", "drawingRevisionRef", "contractorRemarks",
  "distribution", "employersAgentRepresentative", "relatedRfiId",
  "relatedVariationId", "status",
];

// Register workflow statuses — mirror the site instruction register template.
export const SI_STATUS_OPTIONS = ["Outstanding", "In Process", "Completed", "Cancelled"];

export const DEFAULT_INSTRUCTION_CONFIG = {
  documentTitle: "Site Instruction",
  numberingFormat: "SI-{seq:3}",
  customFields: [],
  print: {
    layout: "tabular",
    headerLines: [],
    signatureLeftLabel: "EMPLOYER'S AGENT",
    signatureRightLabel: "CONTRACTOR'S REPRESENTATIVE",
    footerClause: "",
  },
};

export function getInstructionConfig(currentProject) {
  const cfg = currentProject?.instructionConfig || {};
  return {
    ...DEFAULT_INSTRUCTION_CONFIG,
    ...cfg,
    customFields: Array.isArray(cfg.customFields) ? cfg.customFields : [],
    print: { ...DEFAULT_INSTRUCTION_CONFIG.print, ...(cfg.print || {}) },
  };
}

const FIELD_TYPES = ["text", "textarea", "date", "number", "select", "checkbox"];

// ─── Numbering ───────────────────────────────────────────────────────────────
// Format tokens:  {seq} / {seq:N} (zero-padded), {yyyy}, {contract}

export function formatSiNumber(config, seq, currentProject) {
  const fmt = config?.numberingFormat || "SI-{seq:3}";
  return fmt
    .replace(/\{seq:(\d+)\}/g, (_, n) => String(seq).padStart(Number(n), "0"))
    .replace(/\{seq\}/g, String(seq))
    .replace(/\{yyyy\}/g, String(new Date().getFullYear()))
    .replace(/\{contract\}/g, currentProject?.contractNumber || "");
}

export function suggestNextSiNumber(config, instructions = [], currentProject) {
  // Extract trailing digit runs from existing SI numbers; next = max + 1.
  const nums = instructions
    .map((r) => {
      const m = String(r.siNumber || "").match(/(\d+)(?!.*\d)/);
      return m ? parseInt(m[1], 10) : NaN;
    })
    .filter((n) => Number.isFinite(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return formatSiNumber(config, next, currentProject);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const escapeHtml = (v) =>
  String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const keyFromLabel = (label) =>
  String(label || "")
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(/\s+/)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("") || `field${Date.now().toString(36)}`;

// ─── CustomFieldsInputs — mounts inside the SI form ─────────────────────────
// values  = instructionForm.customFields (a {key: value} map)
// onChange(nextMap) — caller wires it to updateForm("customFields", nextMap)

export function CustomFieldsInputs({ config, values = {}, onChange }) {
  const fields = config?.customFields || [];
  if (!fields.length) return null;

  const set = (key, value) => onChange({ ...(values || {}), [key]: value });

  return (
    <>
      {fields.map((f) => {
        const v = values?.[f.key] ?? (f.type === "checkbox" ? false : "");
        if (f.type === "textarea") {
          return (
            <FormRow key={f.key} label={f.label} span={2}>
              <Textarea rows={2} value={v || ""} onChange={(e) => set(f.key, e.target.value)} />
            </FormRow>
          );
        }
        if (f.type === "select") {
          return (
            <FormRow key={f.key} label={f.label}>
              <Select value={v || ""} onChange={(e) => set(f.key, e.target.value)}>
                <option value="">—</option>
                {(f.options || []).map((o) => <option key={o}>{o}</option>)}
              </Select>
            </FormRow>
          );
        }
        if (f.type === "checkbox") {
          return (
            <FormRow key={f.key} label={f.label}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, padding: "8px 0", cursor: "pointer" }}>
                <input type="checkbox" checked={!!v} onChange={(e) => set(f.key, e.target.checked)} />
                Yes
              </label>
            </FormRow>
          );
        }
        return (
          <FormRow key={f.key} label={f.label}>
            <Input
              type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
              value={v || ""}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </FormRow>
        );
      })}
    </>
  );
}

// ─── Configurable print ──────────────────────────────────────────────────────
// Opens a print window following the same pattern as the W1 certificate PDF.

export function printInstruction(row, config, currentProject) {
  const cfg = { ...DEFAULT_INSTRUCTION_CONFIG, ...config, print: { ...DEFAULT_INSTRUCTION_CONFIG.print, ...(config?.print || {}) } };
  const p = cfg.print;
  const title = cfg.documentTitle || "Site Instruction";

  const headerLines = (p.headerLines && p.headerLines.length
    ? p.headerLines
    : [currentProject?.client || "", currentProject?.contractNumber || "", currentProject?.contractDescription || ""]
  ).filter(Boolean);

  const printFields = (cfg.customFields || []).filter((f) => f.showInPrint !== false);
  const customRows = printFields
    .map((f) => {
      const v = row.customFields?.[f.key];
      const display = f.type === "checkbox" ? (v ? "Yes" : "No") : (v ?? "");
      if (display === "" || display === undefined || display === null) return "";
      return `<tr><td class="label">${escapeHtml(f.label)}</td><td>${escapeHtml(display)}</td></tr>`;
    })
    .join("");

  const meta = [
    ["Contract No", row.contractNumber || currentProject?.contractNumber || ""],
    ["Contractor", row.contractor || currentProject?.contractor || ""],
    ["Date", row.instructionDate || ""],
    ["Issued By", row.issuedBy || ""],
    ["Financial Implications", row.financialImplications || ""],
    ["Valuation Basis", row.valuationBasis || ""],
    ["Drawing / Revision", row.drawingRevisionRef || ""],
  ].filter(([, v]) => v);

  const metaRows = meta
    .map(([k, v]) => `<tr><td class="label">${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`)
    .join("");

  const isLetter = p.layout === "letter";

  const body = isLetter
    ? `
      <p class="ref"><strong>${escapeHtml(title)} No: ${escapeHtml(row.siNumber || "")}</strong></p>
      <p class="ref">Date: ${escapeHtml(row.instructionDate || "")}</p>
      <p class="subject"><strong>RE: ${escapeHtml(row.subject || "")}</strong></p>
      <p>In terms of the contract you are hereby instructed as follows:</p>
      <div class="instruction-body">${escapeHtml(row.instructionText || "").replace(/\n/g, "<br/>")}</div>
      ${customRows ? `<table class="meta-table">${customRows}</table>` : ""}
      ${row.contractorRemarks ? `<p><strong>Contractor's Remarks:</strong> ${escapeHtml(row.contractorRemarks)}</p>` : ""}
    `
    : `
      <table class="meta-table">
        <tr><td class="label">${escapeHtml(title)} No</td><td class="mono"><strong>${escapeHtml(row.siNumber || "")}</strong></td></tr>
        ${metaRows}
        ${customRows}
      </table>
      <div class="box">
        <div class="box-title">Subject</div>
        <div>${escapeHtml(row.subject || "")}</div>
      </div>
      <div class="box">
        <div class="box-title">Instruction</div>
        <div class="instruction-body">${escapeHtml(row.instructionText || "").replace(/\n/g, "<br/>")}</div>
      </div>
      ${row.contractorRemarks ? `<div class="box"><div class="box-title">Contractor's Remarks</div><div>${escapeHtml(row.contractorRemarks)}</div></div>` : ""}
    `;

  openHtmlWindow(`<!doctype html><html><head><title>${escapeHtml(title)} ${escapeHtml(row.siNumber || "")}</title>
<style>
@page { size: A4 portrait; margin: 14mm; }
* { box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; color: #111827; font-size: 12px; line-height: 1.45; margin: 0; padding: 0; }
.print-actions { margin: 0 0 14px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; display: flex; justify-content: flex-end; }
.print-actions button { padding: 8px 14px; border: none; border-radius: 8px; background: #1B3A57; color: #fff; font-weight: 700; cursor: pointer; }
@media print { .print-actions { display: none; } }
.header { border-bottom: 3px solid #1B3A57; padding-bottom: 10px; margin-bottom: 14px; }
.header .lines { font-size: 11px; text-transform: uppercase; font-weight: 700; line-height: 1.35; }
.doc-title { text-align: center; font-size: 15px; font-weight: 700; text-transform: uppercase; border: 2px solid #111827; padding: 6px; margin: 12px 0; letter-spacing: 0.04em; }
.meta-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
.meta-table td { border: 1px solid #111827; padding: 5px 8px; vertical-align: top; }
.meta-table td.label { width: 32%; font-weight: 700; background: #f3f4f6; }
.mono { font-family: "Courier New", monospace; }
.box { border: 1px solid #111827; margin: 10px 0; }
.box-title { background: #f3f4f6; border-bottom: 1px solid #111827; padding: 4px 8px; font-weight: 700; font-size: 11px; text-transform: uppercase; }
.box > div:last-child { padding: 8px; min-height: 28px; }
.instruction-body { white-space: pre-wrap; min-height: 90px; }
.ref { margin: 3px 0; }
.subject { margin: 14px 0 8px; }
.footer-clause { font-size: 10.5px; color: #374151; margin-top: 14px; }
.signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 2px solid #111827; margin-top: 18px; page-break-inside: avoid; }
.sig-box { min-height: 100px; padding: 8px 12px; display: flex; flex-direction: column; justify-content: flex-end; }
.sig-box:first-child { border-right: 2px solid #111827; }
.sig-line { border-top: 1px solid #111827; padding-top: 4px; font-weight: 700; text-transform: uppercase; font-size: 10.5px; text-align: center; margin-top: 30px; }
.sig-date { border-top: 1px solid #111827; padding-top: 4px; font-size: 10px; text-align: center; margin-top: 14px; }
</style></head><body>
<div class="print-actions"><button onclick="window.print()">Print / Save as PDF</button></div>
<div class="header"><div class="lines">${headerLines.map((l) => `<div>${escapeHtml(l)}</div>`).join("")}</div></div>
<div class="doc-title">${escapeHtml(title)}</div>
${body}
${p.footerClause ? `<p class="footer-clause">${escapeHtml(p.footerClause)}</p>` : ""}
<div class="signatures">
  <div class="sig-box"><div class="sig-line">${escapeHtml(p.signatureLeftLabel || "EMPLOYER'S AGENT")}</div><div class="sig-date">Date</div></div>
  <div class="sig-box"><div class="sig-line">${escapeHtml(p.signatureRightLabel || "CONTRACTOR'S REPRESENTATIVE")}</div><div class="sig-date">Date</div></div>
</div>
</body></html>`);
}

// ─── Instruction register print ──────────────────────────────────────────────
// One-page register of every SI on the project, matching the site instruction
// register template: header block (contract, register date, addendum, status
// counts) + a Date Issued / SI No / Subject / Status / ER / Received By /
// Date Received table.

const toInitials = (name) => {
  const v = String(name || "").trim();
  if (!v) return "";
  const words = v.split(/\s+/);
  return words.length > 1 ? words.map((word) => word.charAt(0).toUpperCase()).join("") : v;
};

export function printInstructionRegister(rows, config, currentProject) {
  const cfg = { ...DEFAULT_INSTRUCTION_CONFIG, ...config, print: { ...DEFAULT_INSTRUCTION_CONFIG.print, ...(config?.print || {}) } };
  const title = `${cfg.documentTitle || "Site Instruction"} Register`;

  const headerLines = (cfg.print.headerLines && cfg.print.headerLines.length
    ? cfg.print.headerLines
    : [currentProject?.client || "", currentProject?.contractNumber || "", currentProject?.contractDescription || currentProject?.name || ""]
  ).filter(Boolean);

  // Register order: SI number ascending (trailing digits), then issue date.
  const sorted = [...rows].sort((a, b) => {
    const num = (r) => {
      const m = String(r.siNumber || "").match(/(\d+)(?!.*\d)/);
      return m ? parseInt(m[1], 10) : Infinity;
    };
    const d = num(a) - num(b);
    if (d) return d;
    return String(a.instructionDate || a.date || "").localeCompare(String(b.instructionDate || b.date || ""));
  });

  const statusCounts = SI_STATUS_OPTIONS.map((s) => [s, rows.filter((r) => r.status === s).length]);

  const metaLeft = [
    ["Contract Number", currentProject?.contractNumber || currentProject?.contractNo || ""],
    ["Contract Description", currentProject?.contractDescription || currentProject?.name || ""],
    ["Register Date", new Date().toISOString().slice(0, 10)],
    ["Addendum No", "N/A"],
  ];

  const bodyRows = sorted
    .map((r) => `<tr>
      <td class="mono">${escapeHtml(r.instructionDate || r.date || "")}</td>
      <td class="mono center"><strong>${escapeHtml(r.siNumber || "")}</strong></td>
      <td>${escapeHtml(r.subject || r.instructionDescription || "")}</td>
      <td class="center status-${escapeHtml(String(r.status || "").replace(/\s+/g, "-").toLowerCase())}">${escapeHtml(r.status || "")}</td>
      <td class="center">${escapeHtml(toInitials(r.employersAgentRepresentative || r.issuedBy))}</td>
      <td class="center">${escapeHtml(toInitials(r.receivedBy))}</td>
      <td class="mono">${escapeHtml(r.receivedDate || "")}</td>
    </tr>`)
    .join("");

  openHtmlWindow(`<!doctype html><html><head><title>${escapeHtml(title)}</title>
<style>
@page { size: A4 portrait; margin: 14mm; }
* { box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; color: #111827; font-size: 11.5px; line-height: 1.4; margin: 0; padding: 0; }
.print-actions { margin: 0 0 14px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; display: flex; justify-content: flex-end; }
.print-actions button { padding: 8px 14px; border: none; border-radius: 8px; background: #1B3A57; color: #fff; font-weight: 700; cursor: pointer; }
@media print { .print-actions { display: none; } }
.header { border-bottom: 3px solid #1B3A57; padding-bottom: 10px; margin-bottom: 14px; }
.header .lines { font-size: 11px; text-transform: uppercase; font-weight: 700; line-height: 1.35; }
.doc-title { text-align: center; font-size: 15px; font-weight: 700; text-transform: uppercase; border: 2px solid #111827; padding: 6px; margin: 12px 0; letter-spacing: 0.04em; }
.meta-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 10px; margin: 10px 0; align-items: start; }
.meta-table, .reg-table { width: 100%; border-collapse: collapse; }
.meta-table td { border: 1px solid #111827; padding: 5px 8px; vertical-align: top; }
.meta-table td.label { width: 40%; font-weight: 700; background: #f3f4f6; }
.reg-table th { border: 1px solid #111827; padding: 5px 6px; background: #1B3A57; color: #fff; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.03em; }
.reg-table td { border: 1px solid #111827; padding: 4px 6px; vertical-align: top; }
.mono { font-family: "Courier New", monospace; }
.center { text-align: center; }
.status-completed { color: #166534; font-weight: 700; }
.status-in-process { color: #92400e; font-weight: 700; }
.status-outstanding { color: #b91c1c; font-weight: 700; }
.status-cancelled { color: #6b7280; font-weight: 700; text-decoration: line-through; }
.count { font-family: "Courier New", monospace; font-weight: 700; text-align: center; width: 34%; }
</style></head><body>
<div class="print-actions"><button onclick="window.print()">Print / Save as PDF</button></div>
<div class="header"><div class="lines">${headerLines.map((l) => `<div>${escapeHtml(l)}</div>`).join("")}</div></div>
<div class="doc-title">${escapeHtml(title)}</div>
<div class="meta-grid">
  <table class="meta-table">
    ${metaLeft.map(([k, v]) => `<tr><td class="label">${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`).join("")}
  </table>
  <table class="meta-table">
    ${statusCounts.map(([k, v]) => `<tr><td class="label">${escapeHtml(k)}</td><td class="count">${v}</td></tr>`).join("")}
  </table>
</div>
<table class="reg-table">
  <thead>
    <tr><th>Date Issued</th><th>SI No.</th><th>Subject</th><th>Status</th><th>ER</th><th>Received By</th><th>Date Received</th></tr>
  </thead>
  <tbody>${bodyRows || `<tr><td colspan="7" class="center">No site instructions recorded.</td></tr>`}</tbody>
</table>
</body></html>`);
}

// ─── AI format extraction ────────────────────────────────────────────────────
// Upload a sample SI (PDF or image) → the proxy extracts a config schema.

const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

async function extractFormatFromSample(file) {
  if (!AI_PROXY_URL) throw new Error("AI proxy not configured (REACT_APP_AI_PROXY_URL).");

  const base64 = await fileToBase64(file);
  const isPdf = file.type === "application/pdf";
  const contentBlock = isPdf
    ? { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } }
    : { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } };

  const system =
    "You are a construction contract administration assistant. You analyse a client's sample site " +
    "instruction form and extract its format as JSON config. Respond with ONLY a JSON object — no " +
    "markdown fences, no preamble.";

  const user =
`Analyse this sample site instruction document and extract its format.

The app already has these CANONICAL fields — do NOT include them as custom fields:
${CANONICAL_FIELDS.join(", ")}

Return ONLY this JSON shape:
{
  "documentTitle": "the document's heading, e.g. Engineer's Instruction",
  "numberingFormat": "pattern with {seq} or {seq:N} for the sequence, {yyyy} for year, {contract} for contract number — inferred from the sample's numbering, e.g. EI-{yyyy}-{seq:2}",
  "customFields": [
    { "key": "camelCaseKey", "label": "Label As Printed", "type": "text|textarea|date|number|select|checkbox", "options": ["only for select"], "showInPrint": true }
  ],
  "print": {
    "layout": "tabular or letter — tabular if the form is boxed/grid style, letter if it reads as correspondence",
    "headerLines": ["each header/letterhead text line, top to bottom"],
    "signatureLeftLabel": "left signature block label",
    "signatureRightLabel": "right signature block label",
    "footerClause": "any standing clause/condition text printed at the foot of the form"
  }
}

Only include genuinely distinct custom fields that the canonical set does not cover. Keep keys short camelCase.`;

  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const u = getAuth().currentUser;
    if (u) idToken = await u.getIdToken();
  } catch (_) { /* dev */ }

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), ...(await appCheckHeader()) },
    body: JSON.stringify({
      model: "claude-sonnet-4-5",
      max_tokens: 2000,
      system,
      messages: [{ role: "user", content: [contentBlock, { type: "text", text: user }] }],
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || err?.error || `AI proxy HTTP ${res.status}`);
  }
  const data = await res.json();
  const text = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
  const clean = text.replace(/```json|```/g, "").trim();
  const parsed = JSON.parse(clean);

  // Sanitise: drop canonical collisions, coerce types, ensure keys.
  const seen = new Set();
  const customFields = (Array.isArray(parsed.customFields) ? parsed.customFields : [])
    .map((f) => ({
      key: (f.key && /^[a-zA-Z][a-zA-Z0-9]*$/.test(f.key) ? f.key : keyFromLabel(f.label)),
      label: String(f.label || f.key || "").trim(),
      type: FIELD_TYPES.includes(f.type) ? f.type : "text",
      options: Array.isArray(f.options) ? f.options.map(String) : [],
      showInPrint: f.showInPrint !== false,
    }))
    .filter((f) => f.label && !CANONICAL_FIELDS.includes(f.key) && !seen.has(f.key) && seen.add(f.key));

  return {
    documentTitle: String(parsed.documentTitle || "Site Instruction"),
    numberingFormat: String(parsed.numberingFormat || "SI-{seq:3}"),
    customFields,
    print: {
      layout: parsed.print?.layout === "letter" ? "letter" : "tabular",
      headerLines: Array.isArray(parsed.print?.headerLines) ? parsed.print.headerLines.map(String) : [],
      signatureLeftLabel: String(parsed.print?.signatureLeftLabel || "EMPLOYER'S AGENT"),
      signatureRightLabel: String(parsed.print?.signatureRightLabel || "CONTRACTOR'S REPRESENTATIVE"),
      footerClause: String(parsed.print?.footerClause || ""),
    },
  };
}

// ─── Panel styles ────────────────────────────────────────────────────────────

const s = {
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, fontFamily: "inherit", outline: "none" },
  small: { padding: "5px 9px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  h4: { margin: "0 0 8px", fontSize: 12, fontWeight: 800, color: C.ink, textTransform: "uppercase", letterSpacing: "0.05em" },
  hint: { fontSize: 11.5, color: C.inkMuted, margin: "4px 0 0" },
  mono: { fontFamily: MONO },
};

// ─── Main panel ──────────────────────────────────────────────────────────────

export default function InstructionFormatPanel({ currentProject, currentProjectId, onClose }) {
  const projectId = currentProject?.id || currentProjectId;
  const [cfg, setCfg] = useState(() => getInstructionConfig(currentProject));
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setPrint = (patch) => setCfg((c) => ({ ...c, print: { ...c.print, ...patch } }));

  // ── Field list ops ──
  const addField = () =>
    set({ customFields: [...cfg.customFields, { key: `field${cfg.customFields.length + 1}`, label: "", type: "text", options: [], showInPrint: true }] });

  const updateField = (i, patch) => {
    const next = [...cfg.customFields];
    next[i] = { ...next[i], ...patch };
    // Regenerate key from label while the field is new (key not manually meaningful yet)
    if (patch.label !== undefined) next[i].key = keyFromLabel(patch.label);
    set({ customFields: next });
  };

  const removeField = (i) => set({ customFields: cfg.customFields.filter((_, j) => j !== i) });

  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cfg.customFields.length) return;
    const next = [...cfg.customFields];
    [next[i], next[j]] = [next[j], next[i]];
    set({ customFields: next });
  };

  // ── AI import ──
  const runImport = async (file) => {
    setImportBusy(true);
    setImportError("");
    setImportPreview(null);
    try {
      const extracted = await extractFormatFromSample(file);
      setImportPreview(extracted);
    } catch (e) {
      setImportError(e.message || "Extraction failed");
    } finally {
      setImportBusy(false);
    }
  };

  const applyImport = () => {
    if (!importPreview) return;
    // Merge: imported fields are appended after existing ones, skipping duplicate keys.
    const existingKeys = new Set(cfg.customFields.map((f) => f.key));
    const merged = [...cfg.customFields, ...importPreview.customFields.filter((f) => !existingKeys.has(f.key))];
    setCfg({ ...importPreview, customFields: merged });
    setImportPreview(null);
  };

  // ── Save ──
  const save = useCallback(async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "projects", projectId), {
        instructionConfig: cfg,
        instructionConfigUpdatedAt: serverTimestamp(),
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } finally {
      setSaving(false);
    }
  }, [projectId, cfg]);

  const numberPreview = useMemo(() => formatSiNumber(cfg, 7, currentProject), [cfg, currentProject]);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "32px 16px" }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(820px, 100%)", padding: 24, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", fontFamily: t.font?.body }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontFamily: t.font?.display, color: C.ink }}>Instruction Format — This Project</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {savedFlash && <span style={{ fontSize: 12, fontWeight: 700, color: C.success }}>✓ Saved</span>}
            <Button size="sm" variant="subtle" onClick={onClose}>Close</Button>
            <Button size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Format"}</Button>
          </div>
        </div>
        <p style={{ ...s.hint, marginBottom: 18 }}>
          Canonical fields (SI number, dates, subject, instruction text, financial/as-built flags, valuation basis)
          are fixed across all projects — these settings add client-specific fields and control the printed form only.
        </p>

        {/* ── AI import ── */}
        <div style={{ padding: "14px 16px", background: C.primarySoft, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 20 }}>
          <h4 style={s.h4}>✦ Import Client Format</h4>
          <p style={{ ...s.hint, margin: "0 0 10px" }}>
            Upload a sample of the client's site instruction (PDF or photo) and the field schema, numbering pattern,
            header and signature blocks are extracted automatically. Review before applying.
          </p>
          <label style={{ ...s.small, display: "inline-flex", alignItems: "center", gap: 6, opacity: importBusy ? 0.6 : 1 }}>
            {importBusy ? "Analysing sample…" : "📄 Upload Sample SI"}
            <input type="file" accept="application/pdf,image/*" style={{ display: "none" }} disabled={importBusy}
                   onChange={(e) => { if (e.target.files?.[0]) runImport(e.target.files[0]); e.target.value = ""; }} />
          </label>
          {importError && <p style={{ margin: "8px 0 0", fontSize: 12, fontWeight: 600, color: C.danger }}>{importError}</p>}

          {importPreview && (
            <div style={{ marginTop: 12, padding: "12px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <p style={{ margin: "0 0 6px", fontSize: 12.5 }}>
                <strong>Extracted:</strong> "{importPreview.documentTitle}" · numbering <span style={s.mono}>{importPreview.numberingFormat}</span> ·
                {" "}{importPreview.customFields.length} custom field{importPreview.customFields.length === 1 ? "" : "s"} ·
                {" "}{importPreview.print.layout} layout
              </p>
              {importPreview.customFields.length > 0 && (
                <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12 }}>
                  {importPreview.customFields.map((f) => (
                    <li key={f.key}>{f.label} <span style={{ color: C.inkMuted }}>({f.type}{f.options?.length ? `: ${f.options.join(", ")}` : ""})</span></li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Button size="sm" variant="accent" onClick={applyImport}>Apply to Config</Button>
                <Button size="sm" variant="subtle" onClick={() => setImportPreview(null)}>Discard</Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Identity & numbering ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20, minWidth: 0 }}>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Document Title</label>
            <input style={s.inp} value={cfg.documentTitle} placeholder="Site Instruction"
                   onChange={(e) => set({ documentTitle: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Numbering Format</label>
            <input style={{ ...s.inp, ...s.mono }} value={cfg.numberingFormat}
                   onChange={(e) => set({ numberingFormat: e.target.value })} />
            <p style={s.hint}>
              Tokens: <span style={s.mono}>{"{seq}"}</span>, <span style={s.mono}>{"{seq:3}"}</span> (padded),{" "}
              <span style={s.mono}>{"{yyyy}"}</span>, <span style={s.mono}>{"{contract}"}</span> → preview:{" "}
              <strong style={s.mono}>{numberPreview}</strong>
            </p>
          </div>
        </div>

        {/* ── Custom fields ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h4 style={{ ...s.h4, margin: 0 }}>Custom Fields</h4>
            <button style={s.small} onClick={addField}>+ Add Field</button>
          </div>
          {cfg.customFields.length === 0 ? (
            <p style={{ ...s.hint, margin: 0 }}>No custom fields — the form shows canonical fields only.</p>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {cfg.customFields.map((f, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 2fr) minmax(100px, 1fr) minmax(120px, 2fr) auto auto", gap: 8, alignItems: "center", padding: "8px 10px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 0 }}>
                  <input style={{ ...s.inp, minWidth: 0 }} value={f.label} placeholder="Field label"
                         onChange={(e) => updateField(i, { label: e.target.value })} />
                  <select style={{ ...s.inp, minWidth: 0 }} value={f.type} onChange={(e) => updateField(i, { type: e.target.value })}>
                    {FIELD_TYPES.map((ty) => <option key={ty}>{ty}</option>)}
                  </select>
                  {f.type === "select" ? (
                    <input style={{ ...s.inp, minWidth: 0 }} value={(f.options || []).join(", ")} placeholder="Options, comma-separated"
                           onChange={(e) => updateField(i, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} />
                  ) : (
                    <span style={{ ...s.hint, ...s.mono, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.key}>
                      key: {f.key}
                    </span>
                  )}
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, whiteSpace: "nowrap", cursor: "pointer" }}>
                    <input type="checkbox" checked={f.showInPrint !== false} onChange={(e) => updateField(i, { showInPrint: e.target.checked })} />
                    Print
                  </label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={s.small} title="Move up" onClick={() => moveField(i, -1)}>↑</button>
                    <button style={s.small} title="Move down" onClick={() => moveField(i, 1)}>↓</button>
                    <button style={{ ...s.small, color: C.danger, borderColor: C.dangerBorder }} onClick={() => removeField(i)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p style={s.hint}>Custom fields are record and print only — they never feed financial calculations.</p>
        </div>

        {/* ── Print layout ── */}
        <div>
          <h4 style={s.h4}>Printed Form</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Layout</label>
              <select style={s.inp} value={cfg.print.layout} onChange={(e) => setPrint({ layout: e.target.value })}>
                <option value="tabular">Tabular (boxed GCC-style form)</option>
                <option value="letter">Letter (formal correspondence)</option>
              </select>
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Left Signature Label</label>
              <input style={s.inp} value={cfg.print.signatureLeftLabel} onChange={(e) => setPrint({ signatureLeftLabel: e.target.value })} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Right Signature Label</label>
              <input style={s.inp} value={cfg.print.signatureRightLabel} onChange={(e) => setPrint({ signatureRightLabel: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <label style={s.lbl}>Header Lines (one per line — letterhead text)</label>
              <textarea rows={3} style={{ ...s.inp, resize: "vertical", lineHeight: 1.5 }}
                        value={(cfg.print.headerLines || []).join("\n")}
                        placeholder={"EMPLOYER / CLIENT NAME\nDEPARTMENT\nCONTRACT No. 0000/00"}
                        onChange={(e) => setPrint({ headerLines: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })} />
            </div>
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <label style={s.lbl}>Footer Clause</label>
              <textarea rows={2} style={{ ...s.inp, resize: "vertical", lineHeight: 1.5 }}
                        value={cfg.print.footerClause}
                        placeholder="e.g. Issued in terms of clause 5.10 of the General Conditions of Contract. Failure to comply…"
                        onChange={(e) => setPrint({ footerClause: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
